import React from 'react';
import { PlanDetail, CheckoutConfig, TestimonialConfig } from '../../../types';

export const BillingCycleSelector: React.FC<{
  billingCycle: 'monthly' | 'quarterly' | 'annually';
  onChangeCycle: (cycle: 'monthly' | 'quarterly' | 'annually') => void;
  config: CheckoutConfig;
}> = ({ billingCycle, onChangeCycle, config }) => {
  const qDisc = config.quarterlyDiscountPercentage || 10;
  const yDisc = config.yearlyDiscountPercentage || 20;

  return (
    <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-bold">
      <button
        type="button"
        onClick={() => onChangeCycle('monthly')}
        className={`py-1.5 px-2 rounded-md transition-all ${
          billingCycle === 'monthly'
            ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
        }`}
      >
        Monthly
      </button>
      <button
        type="button"
        onClick={() => onChangeCycle('quarterly')}
        className={`py-1.5 px-2 rounded-md transition-all relative ${
          billingCycle === 'quarterly'
            ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
        }`}
      >
        Quarterly
        {qDisc > 0 && (
          <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.2 rounded font-extrabold">
            -{qDisc}%
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={() => onChangeCycle('annually')}
        className={`py-1.5 px-2 rounded-md transition-all relative ${
          billingCycle === 'annually'
            ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm'
            : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
        }`}
      >
        Yearly
        {yDisc > 0 && (
          <span className="ml-1 text-[9px] bg-indigo-100 text-indigo-700 px-1 py-0.2 rounded font-extrabold">
            -{yDisc}%
          </span>
        )}
      </button>
    </div>
  );
};

export const PlanFeatureList: React.FC<{
  features: string[];
  textColorClass?: string;
  checkColorClass?: string;
}> = ({ features, textColorClass = 'text-slate-600 dark:text-slate-300', checkColorClass = 'text-emerald-500' }) => {
  return (
    <div className="space-y-2 py-2 text-left">
      {features.map((feat, i) => (
        <div key={i} className="flex items-center gap-2 text-xs font-medium">
          <svg className={`w-3.5 h-3.5 shrink-0 ${checkColorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className={textColorClass}>{feat}</span>
        </div>
      ))}
    </div>
  );
};

export const TestimonialCard: React.FC<{ testimonial: TestimonialConfig }> = ({ testimonial }) => {
  if (!testimonial || !testimonial.quote) return null;

  return (
    <div className="p-3.5 rounded-xl bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200/70 dark:border-slate-700 text-left space-y-2.5">
      <div className="flex items-center gap-1 text-amber-400">
        {[...Array(5)].map((_, i) => (
          <svg key={i} className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        ))}
      </div>
      <p className="text-xs italic text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
        "{testimonial.quote}"
      </p>
      <div className="flex items-center gap-2 pt-1">
        {testimonial.avatarUrl ? (
          <img src={testimonial.avatarUrl} alt={testimonial.author} className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
            {testimonial.author ? testimonial.author.charAt(0) : 'U'}
          </div>
        )}
        <div>
          <p className="text-xs font-bold text-slate-900 dark:text-white leading-none">{testimonial.author || 'Satisfied Customer'}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-none">{testimonial.role || 'Verified Buyer'}</p>
        </div>
      </div>
    </div>
  );
};

export const PriceBreakdown: React.FC<{
  plan: PlanDetail;
  billingCycle: 'monthly' | 'quarterly' | 'annually';
  addonsTotal: number;
  discountAmount: number;
  finalTotal: number;
  currencySymbol: string;
}> = ({ plan, billingCycle, addonsTotal, discountAmount, finalTotal, currencySymbol }) => {
  return (
    <div className="space-y-1.5 pt-3 border-t border-slate-200/70 dark:border-slate-800 text-xs">
      <div className="flex justify-between text-slate-500 dark:text-slate-400">
        <span>{plan.tier} ({billingCycle})</span>
        <span className="font-semibold text-slate-700 dark:text-slate-200">{currencySymbol}{plan.price}</span>
      </div>
      {addonsTotal > 0 && (
        <div className="flex justify-between text-slate-500 dark:text-slate-400">
          <span>Add-on services</span>
          <span className="font-semibold text-slate-700 dark:text-slate-200">+{currencySymbol}{addonsTotal}</span>
        </div>
      )}
      {discountAmount > 0 && (
        <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
          <span>Discount</span>
          <span>-{currencySymbol}{discountAmount.toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between items-baseline pt-2 border-t border-slate-200/70 dark:border-slate-800 font-black text-sm text-slate-900 dark:text-white">
        <span>Total Due</span>
        <span className="text-base text-indigo-600 dark:text-indigo-400">{currencySymbol}{finalTotal.toFixed(2)}</span>
      </div>
    </div>
  );
};
